import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * A new page starts at the top - unless the address names a place on it
 * (/#programs), in which case it goes there.
 *
 * Going there once is not enough when arriving from another page. The photos
 * above a home-page section are still loading when the page first draws, so a
 * section found at once is pushed down as they arrive, and "Our Programs" in
 * the menu needed pressing twice to land (14 Sep). It is sought again as the
 * page settles, and left alone the moment the reader scrolls for themselves.
 *
 * Keyed on the location's key too, so pressing the same link a second time
 * after scrolling away still goes back to the section.
 */
export function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const samePage = lastPath.current === pathname;
    lastPath.current = pathname;

    // A sign-in link's token also rides in the hash; it names no place.
    const id = hash && !hash.includes("=") ? decodeURIComponent(hash.slice(1)) : "";
    if (!id) {
      if (!samePage) window.scrollTo(0, 0);
      return;
    }

    let stopped = false;
    const go = (behavior: ScrollBehavior) => {
      if (stopped) return;
      const el = document.getElementById(id);
      if (!el) return;
      // Clear the site's fixed header, which would otherwise cover the
      // heading. A page whose header scrolls away with it needs no room.
      const header = document.querySelector("header");
      const pinned = header && ["fixed", "sticky"].includes(getComputedStyle(header).position);
      const offset = pinned ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior });
    };

    if (samePage) {
      go("smooth");
      return;
    }

    window.scrollTo(0, 0);
    const timers = [0, 150, 400, 900, 1800, 3000].map((ms) => window.setTimeout(() => go("auto"), ms));
    const stop = () => { stopped = true; };
    const events = ["wheel", "touchstart", "keydown", "mousedown"] as const;
    events.forEach((e) => window.addEventListener(e, stop, { passive: true }));
    return () => {
      stopped = true;
      timers.forEach((t) => window.clearTimeout(t));
      events.forEach((e) => window.removeEventListener(e, stop));
    };
  }, [pathname, hash, key]);

  return null;
}
